/**
 * Organization UPI Configuration Map
 * 
 * Supports dynamic UPI IDs configured via Organization Settings (saved in customFields.upiId),
 * with localStorage caching and static fallbacks.
 */

export const ORG_UPI_CONFIG = {
    // Default fallback UPI ID if orgId is not explicitly specified
    DEFAULT: '',
};

const DYNAMIC_UPI_MAP = {};

/**
 * Register/Update dynamic UPI ID for an organization
 */
export const setOrgUpiId = (orgId, upiId) => {
    if (!orgId || !upiId) return;
    const cleanUpi = String(upiId).trim();
    if (!cleanUpi) return;
    DYNAMIC_UPI_MAP[orgId] = cleanUpi;
    try {
        if (typeof window !== 'undefined' && window.localStorage) {
            localStorage.setItem(`org_upi_${orgId}`, cleanUpi);
        }
    } catch (e) {
        // ignore storage error
    }
};

/**
 * Get the configured UPI ID for a specific Organization ID.
 * @param {string} orgId - Organization UUID
 * @param {object} [orgData] - Optional Organization object containing customFields or upiId
 * @returns {string} upiId - Configured UPI ID or default fallback
 */
export const getOrgUpiId = (orgId, orgData = null) => {
    // 1. Check passed orgData object
    if (orgData) {
        const directUpi = orgData.customFields?.upiId || orgData.customFields?.upi_id || orgData.upiId;
        if (directUpi && String(directUpi).trim()) {
            const clean = String(directUpi).trim();
            if (orgId) setOrgUpiId(orgId, clean);
            return clean;
        }
    }

    if (!orgId) return ORG_UPI_CONFIG.DEFAULT;

    // 2. Check dynamic in-memory map
    if (DYNAMIC_UPI_MAP[orgId]) {
        return DYNAMIC_UPI_MAP[orgId];
    }

    // 3. Check localStorage
    try {
        if (typeof window !== 'undefined' && window.localStorage) {
            const stored = localStorage.getItem(`org_upi_${orgId}`);
            if (stored && stored.trim()) {
                DYNAMIC_UPI_MAP[orgId] = stored.trim();
                return stored.trim();
            }
        }
    } catch (e) {
        // ignore storage error
    }

    // 4. Check static map or default
    return ORG_UPI_CONFIG[orgId] || ORG_UPI_CONFIG.DEFAULT;
};

export default getOrgUpiId;
